import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import PrimeConfirmDialog from "../../components/dialog/PrimeConfirmDialog";
import PrimeDialog from "../../components/dialog/PrimeDialog";
import { FormInput } from "../../components/FormInput";
import NoInfo from "../../components/NoInfo";
import PrimeTableFooter from "../../components/PrimeTableFooter";
import { useEditableFlag } from "../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../store/messageStore";
import { AlertService } from "../../utility/service/alert-service";
import { handleFileDownloadRes } from "../../utility/service/api-base";
import {
  deleteCbamInspection,
  exportCbamInventory,
  getCompanyPlaceList,
  queryCbamInspectionList,
} from "../../utility/service/cbam-service";
import { checkDpSuccess, getCompanyType, getFormErrorMessage } from "../../utility/service/tool-service";

const CbamIndex = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    action,
    setAction,
    itemObj,
    setItemObj,
    currentCompany,
    setCurrentCompany,
    currentCbamMasterSeq,
    setCurrentCbamMasterSeq,
  } = useOutletContext();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [companyPlaceList, setCompanyPlaceList] = useState([]);
  const [rows, setRows] = useState([]);
  const [lastRowsSeq, setLastRowsSeq] = useState("");
  // 刪除
  const [delObj, setDelObj] = useState({});
  const [delDialog, setDelDialog] = useState(false);
  const [createButtonFlag, setCreateButtonFlag] = useState(false);
  const [limitDialog, setLimitDialog] = useState(false);
  const isEditable = useEditableFlag("F10603");

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    trigger,
    handleSubmit,
    clearErrors,
    setError,
    resetField,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  useEffect(() => {
    axios_getCompanyPlaceList();
  }, []);

  useEffect(() => {
    const isFromChildPage = sessionStorage.getItem("fromCbamChildPage");
    if (currentCompany && isFromChildPage) {
      axios_queryCbamInspectionList({ companySeq: currentCompany?.companySeq });
      setValue("companySeq", currentCompany?.companySeq);
      sessionStorage.removeItem("fromCbamChildPage");
    }
  }, [currentCompany]);

  //組織邊界廠商清單
  const axios_getCompanyPlaceList = () => {
    checkLoading(loading);
    getCompanyPlaceList()
      .then((res) => {
        if (checkDpSuccess(res)) {
          const data = res.data.RespBody.companyPlaceList || [];
          setCompanyPlaceList(data);
          if (getCompanyType() === "A" && data.length > 0) {
            const firstCompany = data[0];
            setValue("companySeq", firstCompany.companySeq);
            const match = data.find((item) => item.companySeq === firstCompany.companySeq);
            setCurrentCompany(match);
            axios_queryCbamInspectionList({ companySeq: match.companySeq });
          }
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  // CBAM 列表查詢
  const axios_queryCbamInspectionList = (reqBody, readMore = false) => {
    checkLoading(loading);
    queryCbamInspectionList(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          const data = res.data.RespBody.cbamMasterList || [];
          if (data.length > 0) {
            setLastRowsSeq(data[data.length - 1].cbamMasterSeq);
            setRows((prev) => (readMore ? [...prev, ...data] : [...data]));
          } else if (!readMore) {
            setRows([]);
          }
          setCreateButtonFlag(res.data.RespBody.createButtonFlag);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  // 查看更多
  const btnReadMore = () => {
    let reqBody = {
      companySeq: getValues("companySeq"),
      keywordSearch: getValues("keywordSearch"),
      cbamMasterSeq: lastRowsSeq,
    };
    axios_queryCbamInspectionList(reqBody);
  };

  const onSubmit = (data) => {
    axios_queryCbamInspectionList({
      companySeq: data.companySeq,
      keywordSearch: data.keywordSearch,
    });
  };

  const checkKeyDown = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  // 設備製程詳情新增/編輯
  const actionItem = (data = {}, action = "create") => {
    setItemObj(data);
    setCurrentCbamMasterSeq(data?.cbamMasterSeq);
    setAction(action);
    if (action === "create") {
      navigate("create");
    }
    if (action === "detail") {
      navigate("detail");
    }
  };

  const actionBodyTemplate = (rowData, props) => {
    if (rowData) {
      return (
        <>
          <Button
            tooltip={t(`button.detail`)}
            tooltipOptions={{ position: "top" }}
            rounded
            text
            severity="secondary"
            className="p-button-icon-only"
            onClick={() => {
              actionItem(rowData, "detail");
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-eye" />
          </Button>
          {/* 清冊 */}
          <Button
            tooltip={t("button.dataEntrySheet")}
            tooltipOptions={{ position: "top" }}
            rounded
            text
            severity="secondary"
            className="p-button-icon-only"
            onClick={() => {
              exportItem(rowData);
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-file-export" />
          </Button>
          <Button
            tooltip={t("button.delete")}
            tooltipOptions={{ position: "top" }}
            disabled={!isEditable}
            rounded
            text
            severity="secondary"
            className="p-button-icon-only"
            onClick={() => {
              setDelDialog(true);
              setDelObj(rowData);
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-trash" />
          </Button>
        </>
      );
    }
  };

  // 刪除
  const axios_deleteCbamInspection = () => {
    checkLoading(loading);
    deleteCbamInspection({ cbamMasterSeq: delObj?.cbamMasterSeq })
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.delete") + " " + t("message.success"));
          setDelDialog(false);
          setDelObj({});
          axios_queryCbamInspectionList({
            companySeq: currentCompany?.companySeq,
            keywordSearch: getValues("keywordSearch"),
          });
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  // 匯出清冊
  const exportItem = (rowData) => {
    checkLoading(loading);
    exportCbamInventory({ CBAM_MASTER_SEQ: rowData?.cbamMasterSeq })
      .then((res) => {
        if (res?.status === 200) {
          const fileName = `${rowData?.cbamProjectName || "CBAM"}_${rowData?.reportingStartDate ||
            ""}_${rowData?.reportingEndDate || ""}.xlsx`
            .replace(/__/g, "_")
            .replace(/^_|_$/g, "");
          handleFileDownloadRes(res, fileName);
        } else {
          AlertService.ok(res?.data?.ResHeader?.rtnCode, res?.data?.ResHeader?.rtnMsg);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  return (
    <>
      {/*上半區*/}
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid">
              {/* 組織名稱 */}
              <div className="col-12 lg:col-6">
                <label htmlFor="placeName" className="form-label required">
                  {t("organName")}
                </label>
                <Controller
                  name="companySeq"
                  control={control}
                  rules={{ required: { value: true, message: t("validation.required") } }}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      loading={loading.toString() === "true" ? 1 : 0}
                      filter
                      disabled={getCompanyType() === "A"}
                      appendTo={"self"}
                      optionLabel="companyName"
                      optionValue="companySeq"
                      value={field.value}
                      virtualScrollerOptions={{ itemSize: 40 }}
                      defaultValue={""}
                      placeholder={t("plz_chs")}
                      name="companySeq"
                      control={control}
                      options={companyPlaceList}
                      className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                      onChange={(event) => {
                        field.onChange(event.value);
                        setCurrentCompany(companyPlaceList?.filter((item) => item.companySeq === event.value)[0]);
                        axios_queryCbamInspectionList({ companySeq: event.value });
                      }}
                    />
                  )}
                />
                {getFormErrorMessage(errors, "companySeq")}
              </div>
              {/*關鍵字*/}
              <div className={"col-12 lg:col-6"}>
                <FormInput
                  register={register}
                  errors={errors}
                  id="keywordSearch"
                  type="text"
                  placeholder={`${t("projectName")}`}
                  labelClass="control-label"
                  enterClick={checkKeyDown}
                  labelText={t("keyword")}
                />
              </div>
            </div>

            <div className="col-12 gap-2 flex-all-center">
              <Button type="submit" label={t("button.query")} />
              <Button
                outlined
                type="button"
                disabled={!currentCompany?.companySeq || !isEditable}
                onClick={() => {
                  if (!createButtonFlag) {
                    setLimitDialog(true);
                  } else {
                    actionItem();
                  }
                }}
                label={t("button.create")}
              />
            </div>
          </form>
        </div>
      </div>
      {/*下半區*/}
      <div className="p-card">
        <div className="p-card-body">
          <DataTable
            stripedRows
            resizableColumns
            className="white-data-table hover-highlight no-shadow"
            tableStyle={{ minWidth: "55rem" }}
            footer={
              rows?.length > 0 && <PrimeTableFooter rowDataList={rows} isShowMore={true} btnReadMore={btnReadMore} />
            }
            emptyMessage={<NoInfo panel="table" />}
            value={rows}
          >
            <Column field="cbamProjectName" header={t("projectName")}></Column>
            <Column
              header={t("reportingPeriod")}
              body={(rowData) => `${rowData.reportingStartDate} ~ ${rowData.reportingEndDate}`}
            ></Column>
            <Column field="placeName" header={t("place_name")}></Column>
            <Column header={t("action_type")} body={actionBodyTemplate}></Column>
          </DataTable>
        </div>
      </div>

      {/*刪除*/}
      <PrimeConfirmDialog
        visible={delDialog}
        onClose={() => {
          setDelDialog(false);
          setDelObj({});
        }}
        onConfirm={() => {
          axios_deleteCbamInspection();
        }}
        title={t("button.delete")}
        bodyTemplate={
          <div className="form-label">
            {t("product_name")}: {delObj?.cbamProjectName}
          </div>
        }
      />

      {/* CBAM盤查上限 */}
      <PrimeDialog
        visible={limitDialog}
        isShowConfBtn={false}
        onClose={() => {
          setLimitDialog(false);
        }}
        title={t("button.create")}
        bodyTemplate={<>{t("createCountLimitReachedTip")}</>}
      />
    </>
  );
};

export default CbamIndex;
